
import React, { memo } from "react";

export interface LoadingStateProps {
  /**
   * Text message to display during loading
   */
  message?: string;
  
  /**
   * Size of the loading indicator and text
   */
  size?: "small" | "default" | "large";
  
  /**
   * Centered layout for full-page loading
   */
  centered?: boolean;
  
  /**
   * Type of loading animation
   */
  variant?: "spinner" | "dots" | "pulse";
  
  /**
   * CSS class names to apply to wrapper
   */
  className?: string;
}

/**
 * LoadingState component for consistent loading indicators throughout the app
 */
export const LoadingState = memo(function LoadingState({
  message = "Loading...",
  size = "default",
  centered = false,
  variant = "spinner",
  className = "",
}: LoadingStateProps) {
  // Size maps for different elements
  const spinnerSizes = {
    small: "h-3 w-3",
    default: "h-5 w-5",
    large: "h-8 w-8",
  };
  
  const textSizes = {
    small: "text-xs",
    default: "text-sm",
    large: "text-base",
  };
  
  const containerClasses = centered 
    ? "flex flex-col items-center justify-center min-h-[12rem]" 
    : "flex items-center justify-start";
  
  // Render the appropriate loading variant
  const renderLoadingIndicator = () => {
    switch (variant) {
      case "dots":
        return (
          <div className="flex space-x-1">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`${spinnerSizes[size]} rounded-full bg-primary animate-pulse`}
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        );
      
      case "pulse":
        return (
          <div 
            className={`${spinnerSizes[size]} rounded-full bg-primary animate-pulse`}
          />
        );
      
      case "spinner":
      default:
        return (
          <div 
            className={`${spinnerSizes[size]} animate-spin rounded-full border-2 border-t-primary border-r-transparent border-b-primary border-l-transparent`} 
          />
        );
    }
  };
  
  return (
    <div 
      className={`${containerClasses} ${className}`}
      role="status" 
      aria-live="polite"
    >
      <div className={`flex ${centered ? 'flex-col items-center' : 'flex-row items-center'} gap-3`}>
        {renderLoadingIndicator()}
        
        {message && (
          <span className={`${textSizes[size]} ${centered ? 'mt-2' : ''} text-muted-foreground`}>
            {message}
          </span>
        )}
      </div>
    </div>
  );
});

LoadingState.displayName = 'LoadingState';

export default LoadingState;
